import deck from "../deck";

const SUITS = ["hearts", "diamonds", "clubs", "spades"];
const SEATS = ["P1", "P2", "P3", "P4"];

const cardCode = (card) => card.details.card;

const seatTeam = (seat) => (seat === "P1" || seat === "P3" ? "teamA" : "teamB");

// numPlayers defaults to 4 so every existing (2v2 Teams / 4-player
// Cutthroat) call site keeps working unchanged. For 3-player Cutthroat,
// SEATS.slice(0, 3) is exactly P1-P3 in seat order, so wrapping the index
// by numPlayers instead of a hardcoded 4 correctly cycles P1->P2->P3->P1
// without ever handing a turn to the nonexistent P4.
const nextSeat = (seat, numPlayers = 4) =>
  SEATS[(SEATS.indexOf(seat) + 1) % numPlayers];

const cloneDeck = () =>
  deck.map((card) => ({ ...card, details: { ...card.details } }));

const applyTrump = (card, trump) => ({
  ...card,
  details: { ...card.details, trump: card.suit === trump },
});

// Mirrors the legality gate in Table.js's playCard(): you may always lead
// anything, and trump can always be played regardless of what you hold.
// The only restriction: if you hold the led suit, you must play either the
// led suit or trump - you can't dump an unrelated third suit.
//
// mustLeadTrump: the bidder is required to lead the very first trick of the
// hand with a trump card (the traditional "pitch") if they hold any trump -
// only meaningful while leading (trickSuit falsy).
const isLegalPlay = (card, hand, trickSuit, trump, mustLeadTrump) => {
  if (!trickSuit) {
    if (mustLeadTrump && hand.some((c) => c.suit === trump)) {
      return card.suit === trump;
    }
    return true;
  }
  if (card.suit === trump || card.suit === trickSuit) return true;
  const hasTrickSuit = hand.some((c) => c.suit === trickSuit);
  return !hasTrickSuit;
};

const legalMoves = (hand, trickSuit, trump, mustLeadTrump) => {
  const moves = hand.filter((card) =>
    isLegalPlay(card, hand, trickSuit, trump, mustLeadTrump)
  );
  return moves.length ? moves : hand;
};

// Mirrors highestCard() in Table.js: if any trump was played this trick,
// highest trump wins; otherwise highest card of the led suit wins.
const trickWinnerSeat = (trickCards, trickSuit) => {
  const hasTrump = trickCards.some((card) => card.details.trump);
  const pool = hasTrump
    ? trickCards.filter((card) => card.details.trump)
    : trickCards.filter((card) => card.suit === trickSuit);
  let best = pool[0];
  for (const card of pool) {
    if (card.details.value > best.details.value) best = card;
  }
  return best.details.owner;
};

// Mirrors updateScore() in Table.js exactly, including its quirks (High/Low
// always resolve to somebody even if no trump was ever played; Jack is only
// awarded if a trump jack was actually in play).
const scoreHand = (wonTricks, winningBid) => {
  const { teamA, teamB } = wonTricks;
  const combined = [...teamA, ...teamB];
  const trumpCards = combined.filter((card) => card?.details.trump);

  let highestValue = -1;
  let highestValueCard = null;
  let lowestValue = 200;
  let lowestValueCard = null;
  let jackCard = null;

  for (const card of trumpCards) {
    if (card?.details.value > highestValue) {
      highestValue = card.details.value;
      highestValueCard = card;
    }
    if (card?.details.value < lowestValue) {
      lowestValue = card.details.value;
      lowestValueCard = card;
    }
    if (card?.details.value === 11) jackCard = card;
  }

  const teamAGame = teamA.reduce((sum, c) => sum + (c?.details.gamePoints || 0), 0);
  const teamBGame = teamB.reduce((sum, c) => sum + (c?.details.gamePoints || 0), 0);

  let teamAScore = 0;
  let teamBScore = 0;

  const high = teamA.includes(highestValueCard);
  const low = teamA.includes(lowestValueCard);

  high ? teamAScore++ : teamBScore++;
  low ? teamAScore++ : teamBScore++;

  if (jackCard !== null) {
    teamA.includes(jackCard) ? teamAScore++ : teamBScore++;
  }

  teamAGame > teamBGame ? teamAScore++ : teamBScore++;

  let success = true;
  const { bid, team } = winningBid;
  if (team === "teamA" && bid > teamAScore) {
    teamAScore = -bid;
    success = false;
  }
  if (team === "teamB" && bid > teamBScore) {
    teamBScore = -bid;
    success = false;
  }

  return { teamAScore, teamBScore, success };
};

export {
  SUITS,
  SEATS,
  cardCode,
  seatTeam,
  nextSeat,
  cloneDeck,
  applyTrump,
  isLegalPlay,
  legalMoves,
  trickWinnerSeat,
  scoreHand,
};
